import React from 'react';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import { useController, useFormContext } from 'react-hook-form';
import { UseControllerProps } from 'react-hook-form/dist/types/controller';
import { FieldValues } from 'react-hook-form/dist/types';
import { useStyles } from 'shared/forms/styles';
import CircularProgress from '@material-ui/core/CircularProgress';

export type RenderTextFieldProps<T extends FieldValues = FieldValues> = UseControllerProps<T> &
  TextFieldProps & {
    formatValue?: (value: unknown) => string | number;
    customLabel?: React.ReactElement;
    onChange?: (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
    isLoading?: boolean;
  };

const RenderTextField = <T extends FieldValues = FieldValues>({
  helperText,
  error: errorProp,
  defaultValue,
  name,
  id,
  control,
  size,
  rules,
  onChange: onChangeTextField = () => ({}),
  onBlur: onBlurTextField = () => ({}),
  formatValue,
  customLabel: CustomLabel,
  isLoading = false,
  ...props
}: RenderTextFieldProps<T>) => {
  const theme = useTheme();
  const classes = useStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const { control: formControl, setError } = useFormContext<T>() ?? {};

  const {
    field: { value, onChange, onBlur, ref },
    fieldState: { error },
  } = useController({
    name: name,
    defaultValue,
    rules,
    control: control ?? formControl,
  });

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (error?.message && typeof setError === 'function') setError(name, { message: '' });

    onChange(e);
    onChangeTextField(e);
    e.target.value = formatValue ? `${formatValue(e.target.value)}` : e.target.value;
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    onBlur();
    onBlurTextField(e);
  };

  return (
    <div className={classes.wrapper}>
      {!!CustomLabel && CustomLabel}
      <TextField
        id={id ?? name}
        autoComplete={name.includes('email') ? 'on' : 'off'}
        size={size ? size : isMobile ? 'small' : 'medium'}
        value={formatValue ? formatValue(value) : value}
        onChange={handleChange}
        onBlur={handleBlur}
        inputRef={ref}
        error={errorProp ?? !!error}
        helperText={helperText ?? error?.message}
        {...props}
      />
      {isLoading && <CircularProgress size={20} className={classes.loader} />}
    </div>
  );
};

export default RenderTextField;
